import React, { useEffect, useState } from 'react'
import { VideoPlayerSimple as VideoPlayer } from '../components/Video/VideoPlayerSimple'

const UserDashboard = () => {
  const [tokenData, setTokenData] = useState(null)
  const [videos, setVideos] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)

  useEffect(() => {
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    
    if (token) {
      try {
        // Decode token to see user info (basic decode, not secure)
        const payload = JSON.parse(atob(token.split('.')[1]))
        setTokenData(payload)
        
        // Fetch videos
        fetchVideos(token)
      } catch (error) {
        console.error('Token decode error:', error)
      }
    }
  }, [])

  // Also refresh videos when component becomes visible (like after upload)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const token = localStorage.getItem('token')
        if (token) {
          fetchVideos(token)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const fetchVideos = async (token) => {
    console.log('UserDashboard: Fetching videos with token:', token ? 'PRESENT' : 'MISSING')
    try {
      const response = await fetch('/.netlify/functions/videos-list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      console.log('UserDashboard: Video fetch response status:', response.status)
      const data = await response.json()
      console.log('UserDashboard: Video fetch data:', data)

      if (data.videos) {
        console.log('UserDashboard: Setting videos:', data.videos.length, 'videos found')
        setVideos(data.videos)
      } else {
        console.log('UserDashboard: No videos in response, setting empty array')
        setVideos([])
      }
    } catch (error) {
      console.error('Videos fetch error:', error)
      setVideos([])
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    window.location.href = '/login'
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-red-900 text-white">
      {/* Navigation Header */}
      <nav className="bg-black/80 backdrop-blur-sm border-b border-red-800/30 p-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-red-500">
              Video<span className="text-white">Share</span><span className="text-red-400">WEB</span>
            </h1>
            <span className="text-gray-400 text-sm">Private Family Video Platform</span>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Admin Link for admin users */}
            {tokenData && tokenData.role === 'admin' && (
              <a 
                href="/admin" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
              >
                Admin
              </a>
            )}
            
            {tokenData && (
              <div className="text-right">
                <div className="text-sm text-white font-medium">{tokenData.email}</div>
                <div className="text-xs text-gray-400 capitalize">{tokenData.role}</div>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-red-500">Welcome Back</span>
          </h1>
          <p className="text-gray-300">Your family videos are ready to watch</p>
        </div>
        

        {/* User Info Card - Compact & Styled */}
        {tokenData && (
          <div className="bg-black/40 backdrop-blur-sm border border-red-800/30 p-6 rounded-xl shadow-xl mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-red-400 mb-2">Account Details</h2>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-300"><span className="text-gray-500">Email:</span> {tokenData.email}</p>
                  <p className="text-gray-300"><span className="text-gray-500">Role:</span> <span className="capitalize text-red-400">{tokenData.role}</span></p>
                </div>
              </div>
              <div className="text-right text-xs text-gray-400">
                <p>Session expires:</p>
                <p>{new Date(tokenData.exp * 1000).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        )}

        {/* Videos Section - Netflix-style Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Family Videos</h2>
            <a 
              href="/upload"
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              + Upload Video
            </a>
          </div>
          
          {videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {videos.map((video) => (
                <div
                  key={video.id}
                  onClick={() => setSelectedVideo(video)}
                  className="group bg-black/40 backdrop-blur-sm border border-gray-800/50 rounded-xl overflow-hidden hover:border-red-500/50 transition-all duration-300 transform hover:scale-[1.02] cursor-pointer"
                >
                  {/* Video Thumbnail Placeholder */}
                  <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <svg className="w-12 h-12 text-red-400 opacity-60 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                    
                    {/* Duration Badge */}
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                      {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                    </div>
                  </div>
                  
                  {/* Video Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-white mb-1 group-hover:text-red-400 transition-colors line-clamp-1">{video.title}</h3>
                    <p className="text-gray-400 text-sm mb-2 line-clamp-2">{video.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs bg-red-600/20 text-red-300 px-2 py-1 rounded-full">{video.category}</span>
                      <button className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all text-sm">
                        Watch →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <svg className="mx-auto h-16 w-16 text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 010 2h-1v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 010-2h4zM6 6v14h12V6H6zM8 8h8v2H8V8zM8 12h8v2H8v-2z"/>
              </svg>
              <h3 className="text-lg font-medium text-gray-300 mb-2">No videos yet</h3>
              <p className="text-gray-500 text-sm mb-4">Upload your first family video to get started</p>
              <a 
                href="/upload"
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-colors duration-200 font-medium"
              >
                Upload Your First Video
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  )
}

export default UserDashboard